import React, { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Theme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { QlfCodeArea, insertAtCursor } from '@qualifio/ui-commons/dist/components/qlf-code-area';
import { editor } from 'monaco-editor';
import { faBracketsCurly } from '@fortawesome/pro-solid-svg-icons/faBracketsCurly';
import { faTimes } from '@fortawesome/pro-solid-svg-icons/faTimes';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import { EditorModalContentProps } from '../../../components/EditorModal';
import { Title } from '../../../../shared/layout/Title';
import { useCurrencies } from '../../../../currency/CurrenciesContext';
import IStandaloneCodeEditor = editor.IStandaloneCodeEditor;

export interface CustomHtmlProps {
  content: string;
}

export interface EditorModalProps extends EditorModalContentProps {
  callback: (data: CustomHtmlProps) => void;
  data: CustomHtmlProps;
  locale: string;
}

const useStyles = makeStyles<Theme>(() => ({
  dialogContent: {
    display: 'flex',
    flexDirection: 'column',
    height: '75vh',
  },
  htmlEditor: {
    flexGrow: 1,
  },
  variableButtons: {
    display: 'flex',
    justifyContent: 'left',
    alignItems: 'center',
    marginTop: '20px',
    gap: '10px',
  },
  dialogActions: {
    justifyContent: 'space-between !important',
  },
}));

export function CustomHtmlModal({
  locale,
  callback,
  data,
  closeModal,
}: EditorModalProps): React.ReactElement {
  const { t } = useTranslation();
  const classes = useStyles();
  const [editor, setEditor] = useState<IStandaloneCodeEditor>();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [content, setContent] = useState<string>(data.content);
  const { currencies } = useCurrencies();

  const dynamicVars = useMemo(() => {
    const vars = [
      { name: 'firstName', value: 'firstName' },
      { name: 'lastName', value: 'lastName' },
      { name: 'email', value: 'email' },
      { name: 'level', value: 'level' },
      { name: 'memberCode', value: 'memberCode' },
    ];
    currencies?.forEach((currency, index) => {
      vars.push({
        name: getLocalizedProperty(currency, 'name', locale) || `currency_${index}`,
        value: `currency_amount_${currency.id}`,
      });
    });
    return vars;
  }, [currencies, locale]);

  return (
    <Dialog
      open={true}
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          height: 'auto',
          margin: 0,
          borderRadius: 0,
          padding: '0 40px',
        },
      }}
    >
      <IconButton
        onClick={() => closeModal()}
        sx={{
          zIndex: 2,
          position: 'absolute',
          right: 5,
        }}
      >
        <FontAwesomeIcon icon={faTimes} />
      </IconButton>
      <DialogTitle>
        <Title>{t('editor.custom-html.modal.title')}</Title>
      </DialogTitle>
      <DialogContent className={classes.dialogContent} style={{ padding: 0 }}>
        <QlfCodeArea
          id="custom-html-editor"
          className={classes.htmlEditor}
          defaultValue={content}
          defaultLanguage="html"
          allowFullScreen
          onMount={editor => setEditor(editor)}
          onChange={value => {
            setContent(value || '');
            setIsDirty(true);
          }}
        />
        <div className={classes.variableButtons}>
          <FontAwesomeIcon icon={faBracketsCurly} />
          <p> {t('editor.custom-html.modal.dynamic-variables')} </p>
          {dynamicVars.map(dynamicVar => {
            return (
              <Button
                variant="rounded"
                key={`button-integration-${dynamicVar.name}`}
                onClick={() => {
                  insertAtCursor(editor!, `{{ ${dynamicVar.value} }}`);
                }}
              >
                {dynamicVar.name}
              </Button>
            );
          })}
        </div>
      </DialogContent>

      <Divider />

      <DialogActions className={classes.dialogActions}>
        <Button type="button" color="grey" onClick={() => closeModal()}>
          {t('generic.actions.cancel')}
        </Button>
        <Button
          color="success"
          disabled={!isDirty}
          onClick={() => {
            callback({ content });
            closeModal();
          }}
        >
          {t('editor.embedded-media.modal.apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
